import { ChangeDetectionStrategy, Component, computed, inject, input, signal, effect } from '@angular/core';
import { IGallery } from '@core/models';
import { LazyLoadImageModule } from 'ng-lazyload-image';
import { CarouselFullImage } from '../carousels';
import { Modal } from '@core/services';
import { CdnUrlPipe } from '@shared/pipes';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

/**
 * Componente de galería de imágenes que muestra una colección de imágenes
 * con capacidad de lazy loading y visualización en carousel modal.
 *
 * @example
 * ```html
 * <app-gallery [gallery]="images" />
 * ```
 */
@Component({
  selector: 'app-gallery',
  imports: [LazyLoadImageModule, CarouselFullImage, CdnUrlPipe, MatProgressSpinnerModule],
  templateUrl: "./gallery.component.html",
  styleUrl: "./gallery.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class Gallery {
  /** Servicio modal para mostrar el carousel de imágenes */
  private readonly modal = inject(Modal);

  /** Estado de carga de cada imagen */
  protected readonly loadingImages = signal<boolean[]>([]);

  /**
   * Array de imágenes de la galería
   * @required
   */
  readonly gallery = input.required<IGallery[]>();

  /**
   * Clase CSS computada basada en el número de imágenes para aplicar estilos específicos
   * @returns Nombre de la clase CSS en formato 'gallery-type-{número}'
   */
  protected readonly classNumImages = computed<string>(
    () => `gallery-type-${this.gallery().length}`
  );

  /**
   * Número máximo de imágenes a mostrar en la galería
   * @default 6
   */
  protected readonly MAX_IMAGES = signal<number>(6);

  /**
   * Indica si hay imágenes ocultas que exceden el límite máximo
   * @returns true si hay más imágenes que el máximo permitido
   */
  protected readonly isHiddenImages = computed<boolean>(
    () => this.gallery().length > this.MAX_IMAGES()
  );

  constructor() {
    effect(() => {
      this.loadingImages.set(this.gallery().map(() => true));
    });
  }


  /**
   * Calcula el número de imágenes que no se muestran en la galería
   * @returns Número de imágenes restantes o 0 si no hay imágenes ocultas
   */
  protected readonly numMissingImages = computed<number>(() => {
    const totalImages = this.gallery().length;
    return totalImages > this.MAX_IMAGES() ? totalImages - (this.MAX_IMAGES() - 1) : 0;
  });


  /**
   * Abre el carousel modal con las imágenes de la galería
   * @param index - Índice de la imagen inicial a mostrar en el carousel
   */
  openCarousel(index: number): void {
    const images = this.gallery().map((img) => img.url);
    this.modal.onCarouselImage({
      showContent: true,
      images,
      imageIndex: index
    });
  }
  protected onImageLoaded(index: number): void {
    const current = [...this.loadingImages()];
    current[index] = false;
    this.loadingImages.set(current);
  }

}
